import React from 'react';
import { useRouter } from 'next/router';
import classNames from 'classnames';
import Grid from '@havenengineering/module-shared-library/dist/components/Grid';

import styles from './search-navigator.module.scss';
import useStepSelections from '../../../../hooks/useStepSelections';
import { Query } from '../../../../types/v2/holiday-search';
import { deleteQueryProperties, getFieldsAfterStep, getStep } from '../../../../helpers/redirects';
import { useJourneySteps } from '../../../../hooks/useJourneySteps';
import { handleStepperNavigation } from '../../../../helpers/gtm/gtm-aggregating-journey';
import Icon from '../../widget/icon';
import asset from '../../../../helpers/assets';

type Props = {
  activeStep: string;
  query: Query;
  pdp?: boolean;
};

export const SearchNavigator = ({ activeStep, query, pdp }: Props) => {
  const { push } = useRouter();

  const journeySteps = useJourneySteps(undefined, query);

  const handleButtonClick = (pathname: string, fromStep: string, toStep: string) => {
    handleStepperNavigation(fromStep, toStep);

    const previousStep = getStep(journeySteps, pathname);
    const fieldsAfterStep = getFieldsAfterStep(journeySteps, previousStep);

    const properties = fieldsAfterStep.map((step) => step?.name);
    const updatedQuery = deleteQueryProperties(query, properties as any, [
      'parkCode',
      'parks',
      'arrivalDate',
      'duration',
      'packageType',
    ]);

    push({
      pathname,
      query: {
        ...updatedQuery,
        referringStep: activeStep.toLowerCase(),
      },
    });
  };

  const { stepsWithSelections } = useStepSelections(query);

  const filteredSteps = stepsWithSelections?.filter((step) => step.title !== 'Holiday details');
  const activeStepIndex = filteredSteps?.findIndex((step) => step.name === activeStep);
  const percentage = ((activeStepIndex + 1) / filteredSteps.length) * 100;
  return (
    <>
      <div className={classNames(styles.stepIndicatorRoot, { [styles.pdp]: pdp })}>
        <Grid.Container>
          <Grid.Row justify="center">
            <Grid.Col xs={12} md={12} lg={12}>
              <div className={styles.container}>
                <ul className={styles.list} data-testid="step-list">
                  {filteredSteps?.map(({ name, title, pathName, selection }) => {
                    if (title)
                      return (
                        <li key={title} className={styles.step}>
                          <div className={styles.stepWrapper}>
                            <span className={styles.label}>{name}</span>
                            <div className={styles.stepContainer}>
                              <span className={styles.selection} title={selection}>
                                {selection || '-'}
                              </span>
                              {selection && (
                                <button
                                  type="button"
                                  data-testid={`edit-${name}`}
                                  className={styles.button}
                                  onClick={() =>
                                    handleButtonClick(
                                      pathName,
                                      activeStep.toLowerCase(),
                                      name ? name.toLowerCase() : '',
                                    )
                                  }
                                >
                                  <Icon
                                    className={styles.editIcon}
                                    src={asset('pencil-white.svg')}
                                    alt=""
                                    role="presentation"
                                  />
                                </button>
                              )}
                            </div>
                          </div>
                        </li>
                      );
                    return null;
                  })}
                </ul>
              </div>
            </Grid.Col>
          </Grid.Row>
        </Grid.Container>
      </div>

      <div className={styles.progressIndicatorContainer}>
        <div
          className={classNames(styles.progressIndicator, { [styles.full]: percentage === 100 })}
        >
          <div
            className={styles.progress}
            style={{
              width: `${percentage}%`,
            }}
          />
        </div>
      </div>
    </>
  );
};
